/*
=========================================================
* RingFoods Pain Points Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// @mui icons
import RestaurantIcon from "@mui/icons-material/Restaurant";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import MoodBadIcon from "@mui/icons-material/MoodBad";
import TranslateIcon from "@mui/icons-material/Translate";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StarBorderIcon from "@mui/icons-material/StarBorder";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, accentError, accentWarning } from "pages/RingFoodsHomepage/theme";

function PainPoints() {
  const painCards = [
    {
      icon: RestaurantIcon,
      title: "The Friday Night Avalanche",
      pain: "The phone won't stop ringing during a packed service. Your host is torn between greeting the couple at the door and the caller on hold. Someone hangs up. You'll never know who.",
      color: accentError,
    },
    {
      icon: NightsStayIcon,
      title: "The 2AM Wake-Up Call",
      pain: "Your personal phone rings after hours. Answer half-asleep and fumble through a reservation, or let it go to voicemail and wake up to a bad review from someone who \"couldn't reach anyone.\"",
      color: "#6366F1",
    },
    {
      icon: MoodBadIcon,
      title: "The Screaming Caller",
      pain: "A hostile customer unloads on your youngest employee about a wrong order. That employee is shaken for the rest of the shift. Three weeks later, they quit. You know why.",
      color: accentError,
    },
    {
      icon: TranslateIcon,
      title: "Lost in Translation",
      pain: "A caller speaks a language your staff can't. There's a painful back-and-forth, then they hang up. You just lost a party of 8 to the restaurant down the street.",
      color: accentWarning,
    },
    {
      icon: AccessTimeIcon,
      title: "The 10-Minute FAQ Call",
      pain: "\"What are your hours?\" \"Do you have parking?\" \"Is there a vegan option?\" Thirty calls a day eating staff time for questions that should answer themselves.",
      color: "#0EA5E9",
    },
    {
      icon: StarBorderIcon,
      title: "The Review You Could Have Prevented",
      pain: "An unanswered call becomes a 1-star Google review: \"Tried calling three times, nobody picked up.\" That review lives on your profile forever.",
      color: accentWarning,
    },
  ];

  return (
    <MKBox component="section" py={{ xs: 8, md: 12 }}>
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 10 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Running a Restaurant is Hard Enough. The Phone Makes It Worse.
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}>
              If any of these sound familiar, you&apos;re not alone.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Pain Cards Grid */}
        <Grid container spacing={3}>
          {painCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    borderLeft: `4px solid ${card.color}`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Icon */}
                    <MKBox
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        backgroundColor: `${card.color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <Icon sx={{ fontSize: 26, color: card.color }} />
                    </MKBox>

                    {/* Title */}
                    <MKTypography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
                      {card.title}
                    </MKTypography>

                    {/* Pain Description */}
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                      {card.pain}
                    </MKTypography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </MKBox>
  );
}

export default PainPoints;
